/**
 * The single frontend mirror of the referral module's backend enums.
 *
 * The portal used to carry its own invented vocabularies ("Submitted to Org", "Needs Replacement")
 * that no server value ever matched, so every status check silently failed. These keys are the
 * backend enum names verbatim; `label` is what a person reads and `tone` is the StatusChip colour.
 *
 * Backend sources — keep in step:
 *   ReferralPartnerStatus, PartnerSubmissionState, DocumentRequestStatus,
 *   DocumentRequestItemState, ReferralCommissionStatus, DocumentModuleType,
 *   ReferralApplicationModule (by clientModule), ReferralApplicationRequestStatus.
 */

export const PARTNER_STATUSES = {
  INVITED: { label: "Invited", tone: "info" },
  ONBOARDING: { label: "Onboarding", tone: "warning" },
  ACTIVE: { label: "Active", tone: "success" },
  SUSPENDED: { label: "Suspended", tone: "danger" },
  ARCHIVED: { label: "Archived", tone: "default" },
};

export const LEAD_STATES = {
  DRAFT: { label: "Draft", tone: "default" },
  AWAITING_CLIENT: { label: "Awaiting client", tone: "info" },
  CLIENT_SUBMITTED: { label: "Client submitted", tone: "warning" },
  PARTNER_CONFIRMED: { label: "Confirmed by you", tone: "info" },
  SENT_TO_ORG: { label: "Sent to office", tone: "info" },
  ACTIVATED: { label: "Activated", tone: "success" },
  REJECTED: { label: "Returned", tone: "danger" },
};

export const DOCUMENT_REQUEST_STATUSES = {
  ACTIVE: { label: "Open", tone: "warning" },
  SUBMITTED: { label: "Submitted", tone: "success" },
  REVOKED: { label: "Revoked", tone: "default" },
};

export const DOCUMENT_ITEM_STATES = {
  PENDING: { label: "Outstanding", tone: "warning" },
  FULFILLED: { label: "Received", tone: "success" },
  FLAGGED: { label: "Needs attention", tone: "danger" },
};

export const COMMISSION_STATUSES = {
  ACCRUED: { label: "Earned", tone: "info" },
  APPROVED: { label: "Approved", tone: "warning" },
  PAID: { label: "Paid", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "default" },
};

export const DOCUMENT_MODULE_TYPES = {
  VISA: { label: "Visa", tone: "info" },
  INSURANCE: { label: "Insurance", tone: "info" },
  RPL_CLIENT: { label: "RPL", tone: "info" },
  SKILL_ASSESSMENT: { label: "Skill assessment", tone: "info" },
  ADMISSION: { label: "Admission", tone: "info" },
  OTHER: { label: "Other", tone: "default" },
};

/** Services a partner can request an application for (backend clientModule values). */
export const APPLICATION_MODULES = {
  admission: { label: "Admission", tone: "info" },
  visa: { label: "Visa", tone: "info" },
  insurance: { label: "Insurance", tone: "info" },
  "skill-assessment": { label: "Skill assessment", tone: "info" },
  "rpl-course": { label: "RPL", tone: "info" },
  other: { label: "Other", tone: "info" },
};

export const APPLICATION_REQUEST_STATUSES = {
  PENDING: { label: "With the office", tone: "warning" },
  APPROVED: { label: "Approved", tone: "success" },
  REJECTED: { label: "Returned", tone: "danger" },
};

/** Sentence-case an unmapped enum name rather than rendering a raw SCREAMING_SNAKE value. */
const sentenceCase = (value) => {
  const words = String(value).toLowerCase().replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "—";
};

export const labelFor = (enumMap, value) => {
  if (value === null || value === undefined || value === "") return "—";
  return enumMap?.[value]?.label ?? sentenceCase(value);
};

export const toneFor = (enumMap, value) => enumMap?.[value]?.tone ?? "default";
